"use client";

import { useEffect, useState } from "react";
import { ThemeProvider } from "@npm_leadtech/qr-ui-components";

import { fonts } from "@/fonts/fonts";

import { ErrorFallback } from "@/components/ErrorFallback";

import { isDOMManipulationError, reportDOMErrorToDatadog } from "@/utils/errorReporting";
import { TranslationsProvider } from "@/hocs/TranslationsProvider";
import { DEFAULT_LANGUAGE } from "@/internals/constants/language";
import { getDomainConfig } from "@/sites/config/domainConfigs";
import { THEME as THEME_TYPE } from "@/themes/types";

const THEME = (process.env.NEXT_PUBLIC_THEME || "mojito") as THEME_TYPE;
const THEME_CSS_FILENAME = `${THEME}-full.css`;
const fontClasses = Object.values(fonts)
  .map((font) => font.variable)
  .join(" ");

/**
 * Global error boundary for Next.js App Router.
 * This catches errors that occur during rendering, including:
 * - DOM manipulation conflicts between React and third-party scripts (VWO, GTM, react-svg)
 * - "Failed to execute 'removeChild' on 'Node'" errors during SPA navigation
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [homePath, setHomePath] = useState("/");

  useEffect(() => {
    const hostname = window.location.hostname;
    const config = getDomainConfig(hostname);
    setHomePath(config.homePath);
  }, []);

  useEffect(() => {
    if (isDOMManipulationError(error)) {
      reportDOMErrorToDatadog(error, "global");
    } else {
      console.error("Global error caught:", error);
    }
  }, [error]);

  return (
    <html
      lang="en"
      className={`${fontClasses} theme-${THEME}`}
    >
      <head>
        <link
          rel="stylesheet"
          href={`/${THEME_CSS_FILENAME}`}
        />
      </head>
      <body>
        <TranslationsProvider
          locale={DEFAULT_LANGUAGE}
          namespaces={["error"]}
          resources={undefined}
        >
          <ThemeProvider theme={THEME}>
            <ErrorFallback
              onReset={reset}
              navigateTo={homePath}
              style={{ height: "100vh", padding: "0px 1rem" }}
            />
          </ThemeProvider>
        </TranslationsProvider>
      </body>
    </html>
  );
}
